import React, { useState, useEffect, useContext, useRef } from 'react';
import { Form, Input, Select, DatePicker, Row, Col, Checkbox, message, Upload, Tooltip } from 'antd';
import ValidatedInputNumber from '../../Common/ValidatedInputNumber/ValidatedInputNumber';
import { Modal, Button } from '../../../design-system';
import { BUTTON_PRESETS } from '../../../design-system/constants/buttonPresets';
import { ArrowLeftOutlined, InfoCircleOutlined, SendOutlined, UploadOutlined } from '@ant-design/icons';
import nodeAPI from '../../../service/ApiServiceNode';
import { LEAD_SOURCE_OPTIONS, PROJECT_PLAN_GUIDE_TEXT } from '../../../constants/projectConstants';

import dayjs from 'dayjs';
import './client-project-form.scss';
import { Context } from '../../../context/store';

import { useProjectForm } from './hooks/useProjectForm';
import {
    shouldAutofillProposalDateForLeadStatus,
    shouldRequirePresentationDateForLeadStatus,
    shouldRequireProposalDateForLeadStatus
} from './utils/leadStatusCreateRules';
import { findClientById, resolveClientAutoFillUpdates } from './utils/clientAutoFill';
import { smartDecimalFormatter, smartDecimalParser } from '../../../utils/numberFormatUtils';
import { MONEY_AMOUNTS } from '../../../constants/financialPrecision';
import logger from '../../../utils/logger';



const { Option } = Select;
const { TextArea } = Input;

const ClientProjectForm = ({ open, onClose, onBack, onSuccess, projectType = 'LEAD', initialClient = null }) => {
    const isConfirmed = projectType === 'CONFIRMED';
    const [form] = Form.useForm();
    const [globalState] = useContext(Context);
    const [dataLoading, setDataLoading] = useState(false);
    
    // Use the project form hook (including conditional validation)
    const { getConditionalRules, validateConditionally, triggerRevalidation, handleConditionalFailure } = useProjectForm(form);
    
    // Handle success callback with tab navigation
    

    const [loading, setLoading] = useState(false);
    const [clientDetailsReady, setClientDetailsReady] = useState(false);
    const clientRequest = useRef(0);
    
    // API data states - all initialized as empty arrays
    const [clients, setClients] = useState([]);
    const [clientContacts, setClientContacts] = useState([]);
    const [, setDepartments] = useState([]);
    const [currencies, setCurrencies] = useState([]);
    const [projectTypes, setProjectTypes] = useState([]);
    const [industries, setIndustries] = useState([]);
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [probabilities, setProbabilities] = useState([]);
    
    // Get users from global context or local state
    const contextUsers = globalState?.userDropdownData?.data?.users || [];
    const [users, setUsers] = useState([]);
    
    // No pagination needed - loading all data at once for better UX
    
    // States for users pagination
    useState(0);
    useState(true);
    useState(false);
    useState('');
    
    // Form control states
    const [selectedClient, setSelectedClient] = useState(null);
    const [leadStatus, setLeadStatus] = useState(null);
    const [useUrlForPlan, setUseUrlForPlan] = useState(false);
    const [noPresentation, setNoPresentation] = useState(false);
    const [noVendorCost, setNoVendorCost] = useState(false);

    // Referral input state
    const [showReferralInput, setShowReferralInput] = useState(false);
    const [referralName, setReferralName] = useState('');
    
    // Generic dropdown state management
    const [dropdownStates, setDropdownStates] = useState({});
    
    const setDropdownOpen = (dropdownName, isOpen) => {
        setDropdownStates(prev => ({
            ...prev,
            [dropdownName]: isOpen
        }));
    };
    
    const getDropdownOpen = (dropdownName) => {
        return dropdownStates[dropdownName] || false;
    };
    
    // Track auto-filled fields
    const [autoFilledFields, setAutoFilledFields] = useState(new Set());

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (open) {
            fetchFormData();
            // Set default values based on project type
            if (!isConfirmed) {
                form.setFieldsValue({
                    retainer: 'No',
                    lead: 1,
                    leadDate: dayjs(), // Set today's date for Lead (Qualified)
                    probability: 5  // Default probability for Lead status 1
                });
                setLeadStatus(1); // Set lead status state for validation rules
                // Track auto-filled fields from initial setup
                setAutoFilledFields(new Set(['leadDate', 'probability']));
            } else {
                form.setFieldsValue({ retainer: 'No', lead: 8, probability: 100, status: 'In-Planning', winDate: dayjs() });
                setLeadStatus(8);
                setAutoFilledFields(new Set(['winDate']));
            }
        }
    }, [open, projectType]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (open && initialClient && !dataLoading && clients.length) {
            form.setFieldsValue({ client: initialClient.id });
            handleClientChange(initialClient.id);
        }
    // Client initialization waits for the industry options and client list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialClient, dataLoading, clients]);

    // Debug: Track autoFilledFields changes
    useEffect(() => {
        logger.info('AutoFilledFields updated:', Array.from(autoFilledFields));
    }, [autoFilledFields]);

    const fetchFormData = async () => {
        setDataLoading(true);
        try {
            // Fetch all clients using the new /all endpoint
            try {
                const clientsRes = await nodeAPI.get('/project/clients?view=dropdown');
                
                // Handle the response structure from client controller
                const responseData = clientsRes.data?.data || clientsRes.data || {};
                const clientsData = responseData.data || responseData.clients || [];
                
                if (Array.isArray(clientsData)) {
                    // Add is_active field based on status (1 = active, 0 = inactive)
                    const formattedClients = clientsData.map(client => ({
                        ...client,
                        is_active: client.status === 1 || client.status === '1' || client.status === 'active' || client.status === undefined
                    }));
                    setClients(formattedClients);
                    logger.debug('Loaded all clients:', formattedClients.length);
                } else {
                    logger.warn('Clients data is not an array:', clientsData);
                    setClients([]);
                }
            } catch (error) {
                logger.error('Error fetching all clients:', error);
                setClients([]);
            }

            // Fetch departments
            try {
                const departmentsRes = await nodeAPI.get('/company-directory/department');
                const deptData = departmentsRes.data?.data || departmentsRes.data || [];
                setDepartments(deptData);
            } catch (error) {
                logger.error('Error fetching departments:', error);
            }

            // Fetch users from the new project users endpoint
            try {
                const usersRes = await nodeAPI.get('/project/users');
                const responseData = usersRes.data?.data || usersRes.data || {};
                const fetchedUsers = responseData.users || [];
                
                if (Array.isArray(fetchedUsers)) {
                    setUsers(fetchedUsers);
                    logger.debug('Users fetched from project endpoint:', fetchedUsers.length);
                    
                    // Set current user as default PIC if available and add auto-fill marker
                    const currentUser = globalState?.user;
                    if (currentUser && !form.getFieldValue('personInCharge')) {
                        const userExists = fetchedUsers.find(u => u.id === currentUser.id);
                        if (userExists) {
                            form.setFieldsValue({ personInCharge: currentUser.id });
                        }
                    }
                } else {
                    logger.warn('Users response is not an array:', fetchedUsers);
                    setUsers([]);
                }
            } catch (error) {
                logger.error('Error fetching users:', error);
                
                // Fallback to context users if available
                if (contextUsers && contextUsers.length > 0) {
                    setUsers(contextUsers);
                    logger.debug('Using users from global context as fallback:', contextUsers.length);
                    
                    // Set current user as default PIC if available and add auto-fill marker
                    const currentUser = globalState?.user;
                    if (currentUser && !form.getFieldValue('personInCharge')) {
                        const userExists = contextUsers.find(u => u.id === currentUser.id);
                        if (userExists) {
                            form.setFieldsValue({ personInCharge: currentUser.id });
                        }
                    }
                } else {
                    setUsers([]);
                }
            }
            

            // Fetch currencies from database (same method as InternalProjectForm)
            const currenciesRes = await nodeAPI.get('/project/currency');
            const currencyData = currenciesRes.data?.data?.currencies || 
                               currenciesRes.data?.currencies || 
                               [];
            
            if (Array.isArray(currencyData)) {
                setCurrencies(currencyData);
                logger.debug('Loaded currencies from database:', currencyData.length);
                
                // Set USD as default currency if available
                const usdCurrency = currencyData.find(c => c.iso === 'USD');
                if (usdCurrency && !form.getFieldValue('currency')) {
                    form.setFieldsValue({ currency: usdCurrency.id });
                }
            } else {
                logger.warn('Currency data is not an array:', currencyData);
                setCurrencies([]);
            }

            // Fetch project classifications from database (already sorted alphabetically)
            try {
                const projectTypesRes = await nodeAPI.get('/project/classifications?view=dropdown');
                const responseData = projectTypesRes.data?.data || projectTypesRes.data || {};
                const classData = responseData.data || responseData.classifications || [];
                
                if (Array.isArray(classData)) {
                    setProjectTypes(classData);
                    logger.debug('Loaded project classifications from database:', classData.length);
                } else {
                    logger.warn('Project classifications data is not an array:', classData);
                    setProjectTypes([]);
                }
            } catch (error) {
                logger.error('Error fetching project classifications:', error);
                message.error('Failed to load project classifications. Please refresh the page.');
                setProjectTypes([]);
            }

            // Fetch industries
            try {
                const industriesRes = await nodeAPI.get('/project/clients/industry-classifications');
                const indData = industriesRes.data?.data?.industries || 
                               industriesRes.data?.industries || 
                               industriesRes.data?.data || 
                               industriesRes.data || [];
                
                if (Array.isArray(indData)) {
                    setIndustries(indData);
                } else {
                    logger.warn('Industries is not an array:', indData);
                    setIndustries([]);
                }
            } catch (error) {
                logger.error('Error fetching industries:', error);
                setIndustries([]);
            }

            // Fetch lead statuses
            try {
                const leadStatusRes = await nodeAPI.get('/project/lead-statuses');
                const leadStatusData = leadStatusRes.data?.data || leadStatusRes.data || {};
                const statuses = leadStatusData.leadStatuses || [];
                const probs = leadStatusData.probabilities || [];
                
                if (Array.isArray(statuses)) {
                    setLeadStatuses(statuses);
                }
                if (Array.isArray(probs)) {
                    setProbabilities(probs);
                }
            } catch (error) {
                logger.error('Error fetching lead statuses:', error);
                // Provide default lead statuses if API fails (actual values from DB)
                setLeadStatuses([
                    { id: 1, lead: 1, name: 'Lead (Qualified)', probability: 5 },
                    { id: 2, lead: 2, name: 'First discussion', probability: 10 },
                    { id: 3, lead: 3, name: 'Brief received', probability: 15 },
                    { id: 4, lead: 4, name: 'Work on proposal or quote', probability: 40 },
                    { id: 5, lead: 5, name: 'Proposal sent -> to follow up', probability: 50 },
                    { id: 6, lead: 6, name: 'Verbally Accepted', probability: 80 },
                    { id: 7, lead: 7, name: 'Work on Contract', probability: 90 },
                    { id: 8, lead: 8, name: 'Won (Contract or Quote signed)', probability: 100 },
                    { id: 9, lead: 9, name: 'Lost', probability: 0 }
                ]);
                setProbabilities([0, 5, 10, 15, 40, 50, 80, 90, 100]);
            }

        } catch (error) {
            logger.error('Error in fetchFormData:', error);
            message.error('Some data could not be loaded. Please refresh the page.');
        } finally {
            setDataLoading(false);
        }
    };

    // Client search is now handled client-side with filterOption

    // Load more users when searching or scrolling
    // Users are now loaded from global context, no need for these functions

    // Handle manual field changes to remove auto-filled status
    const handleManualFieldChange = (fieldName) => {
        if (autoFilledFields.has(fieldName)) {
            const newAutoFilled = new Set(autoFilledFields);
            newAutoFilled.delete(fieldName);
            setAutoFilledFields(newAutoFilled);
        }
    };

    const handleClientChange = async (clientId) => {
        const request = ++clientRequest.current;
        setClientDetailsReady(false);
        setSelectedClient(clientId);
        form.setFieldsValue({ clientPersonInCharge: undefined }); // Reset client contact selection
        setClientContacts([]); // Clear previous contacts
        
        // Store the auto-filled updates to apply after contacts are fetched
        let pendingAutoFilled = new Set(autoFilledFields);
        
        // Clear any existing clientPersonInCharge auto-fill marker when changing clients
        // It will be re-added only if there's actual auto-selection (default or single contact)
        pendingAutoFilled.delete('clientPersonInCharge');
        
        // Check if client is confirmed and auto-set lead source and industry
        const selectedClientData = findClientById(clients, clientId);
        const autoFillUpdates = resolveClientAutoFillUpdates(selectedClientData, pendingAutoFilled);
        if (Object.keys(autoFillUpdates.fieldValues).length > 0) {
            form.setFieldsValue(autoFillUpdates.fieldValues);
        }
        pendingAutoFilled = autoFillUpdates.autoFilledFields;
        if (autoFillUpdates.fieldValues.projectIndustries) {
            logger.debug('Auto-filled project industries:', autoFillUpdates.fieldValues.projectIndustries);
        } else if (Object.prototype.hasOwnProperty.call(autoFillUpdates.fieldValues, 'projectIndustries')) {
            logger.debug('Cleared auto-filled project industries');
        }
        
        if (clientId) {
            try {
                const response = await nodeAPI.get(`/project/clients/${clientId}`);
                if (request !== clientRequest.current) return;
                const client = response.data?.data?.client || response.data?.client;
                if (!client) throw new Error('Client details unavailable');
                const names = client.industries || [];
                const ids = names.map(name => industries.find(item => item.name === name)?.id).filter(Boolean);
                if (ids.length !== names.length) throw new Error('Client industries unavailable');
                form.setFieldsValue({ projectIndustries: ids });
                pendingAutoFilled.add('projectIndustries');
                setClientDetailsReady(true);
            } catch (error) {
                if (request !== clientRequest.current) return;
                form.setFieldsValue({ projectIndustries: [] });
                message.error('Unable to load client industry. Please select the client again.');
            }
        }

        // Fetch client contacts
        if (clientId) {
            try {
                // Try the correct endpoint for client contacts (pagination-free)
                const response = await nodeAPI.get(`/project/clients/${clientId}/contacts?view=dropdown`);
                if (request !== clientRequest.current) return;
                
                // Handle various response structures
                const responseData = response.data?.data || response.data || {};
                const contacts = responseData.contacts || responseData.data || responseData || [];
                
                logger.debug('Client contacts response:', contacts);
                
                // Format contacts properly
                const formattedContacts = Array.isArray(contacts) 
                    ? contacts.map(contact => ({
                        id: contact.id || contact.contact_id,
                        firstName: contact.first_name || contact.firstName || '',
                        lastName: contact.last_name || contact.lastName || '',
                        email: contact.email || '',
                        phone: contact.phone || contact.mobile || '',
                        position: contact.position || contact.title || '',
                        fullName: contact.full_name || `${contact.first_name || contact.firstName || ''} ${contact.last_name || contact.lastName || ''}`.trim(),
                        isDefault: contact.is_default || false
                    }))
                    : [];
                    
                setClientContacts(formattedContacts);
                logger.debug('Loaded client contacts:', formattedContacts.length);
                
                // Auto-select default contact if exists, or single contact
                const currentClientPIC = form.getFieldValue('clientPersonInCharge');
                const defaultContact = formattedContacts.find(c => c.isDefault);
                const hasDefaultContact = !!defaultContact;
                const hasMultipleContacts = formattedContacts.length > 1;
                const hasSingleContact = formattedContacts.length === 1;
                
                if (defaultContact && !currentClientPIC?.length) {
                    // Auto-select default contact when no current selection
                    form.setFieldsValue({ clientPersonInCharge: [defaultContact.id] });
                    // Always track as auto-filled when we auto-select
                    pendingAutoFilled.add('clientPersonInCharge');
                    logger.debug('Auto-selected default contact:', defaultContact.fullName);
                } else if (hasSingleContact && !currentClientPIC?.length) {
                    // Auto-select single contact when no current selection
                    form.setFieldsValue({ clientPersonInCharge: [formattedContacts[0].id] });
                    // Always track as auto-filled when we auto-select
                    pendingAutoFilled.add('clientPersonInCharge');
                    logger.debug('Auto-selected single contact:', formattedContacts[0].fullName);
                } else {
                    // Multiple contacts but no default, or already has selection
                    if (pendingAutoFilled.has('clientPersonInCharge')) {
                        // Remove auto-filled status when no auto-selection is happening
                        pendingAutoFilled.delete('clientPersonInCharge');
                        logger.debug('Removed auto-fill marker - no default contact or single contact auto-selection');
                    }
                }
                
                // Auto-expand dropdown logic for create mode
                const shouldExpandDropdown = (
                    hasMultipleContacts && 
                    !hasDefaultContact && 
                    !currentClientPIC?.length
                );
                
                if (shouldExpandDropdown) {
                    logger.debug('Auto-expanding Client PIC dropdown - multiple contacts available but no default contact set');
                    setTimeout(() => setDropdownOpen('clientPIC', true), 100);
                }
                
                // Apply all auto-filled updates at once
                logger.debug('Auto-filled fields after client change:', Array.from(pendingAutoFilled));
                setAutoFilledFields(pendingAutoFilled);
            } catch (error) {
                if (request !== clientRequest.current) return;
                logger.error('Error fetching client contacts:', error);
                
                // Try alternate endpoint structure
                try {
                    const altResponse = await nodeAPI.get('/project/contacts', {
                        params: { client_id: clientId }
                    });
                    
                    if (request !== clientRequest.current) return;
                    const altData = altResponse.data?.data || altResponse.data || {};
                    const altContacts = altData.contacts || altData.data || altData || [];
                    
                    const formattedContacts = Array.isArray(altContacts) 
                        ? altContacts.map(contact => ({
                            id: contact.id || contact.contact_id,
                            firstName: contact.first_name || '',
                            lastName: contact.last_name || '',
                            email: contact.email || '',
                            phone: contact.phone || contact.mobile || '',
                            position: contact.position || '',
                            fullName: contact.full_name || `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
                            isDefault: contact.is_default || false
                        }))
                        : [];
                        
                    setClientContacts(formattedContacts);
                    logger.debug('Loaded client contacts from alt endpoint:', formattedContacts.length);
                    
                    // Auto-select default contact if exists, or single contact
                    const defaultContact = formattedContacts.find(c => c.isDefault);
                    if (defaultContact) {
                        form.setFieldsValue({ clientPersonInCharge: [defaultContact.id] });
                        // Track as auto-filled only if we're actually setting a value
                        pendingAutoFilled.add('clientPersonInCharge');
                    } else if (formattedContacts.length === 1) {
                        form.setFieldsValue({ clientPersonInCharge: [formattedContacts[0].id] });
                        // Track as auto-filled only for single contact auto-selection
                        pendingAutoFilled.add('clientPersonInCharge');
                    } else {
                        // Multiple contacts but no default - remove auto-filled status if it exists
                        if (pendingAutoFilled.has('clientPersonInCharge')) {
                            pendingAutoFilled.delete('clientPersonInCharge');
                        }
                    }
                    
                    // Apply all auto-filled updates at once
                    logger.debug('Auto-filled fields after client change (alt):', Array.from(pendingAutoFilled));
                    setAutoFilledFields(pendingAutoFilled);
                } catch (altError) {
                    logger.error('Error fetching client contacts from alt endpoint:', altError);
                    setClientContacts([]);
                    // Apply auto-filled updates even if contacts fail
                    logger.debug('Auto-filled fields after client change (no contacts):', Array.from(pendingAutoFilled));
                    setAutoFilledFields(pendingAutoFilled);
                }
            }
        } else {
            setClientContacts([]);
            // Apply auto-filled updates when no client selected
            logger.debug('Auto-filled fields after client change (no client):', Array.from(pendingAutoFilled));
            setAutoFilledFields(pendingAutoFilled);
        }
    };

    const handleClientSelect = (clientId) => {
        // AntD Select does not fire onChange when the user selects the same
        // client again. Re-run the client autofill path so cleared fields can
        // be populated again without changing to another client first.
        if (String(clientId) === String(selectedClient)) {
            handleClientChange(clientId);
        }
    };

    const handleLeadChange = (value) => {
        setLeadStatus(value);
        
        // Find the selected lead status from the dynamic data
        const selectedStatus = Array.isArray(leadStatuses) ? leadStatuses.find(status => status.id === value) : null;

        // Create new set for auto-filled fields
        const newAutoFilled = new Set(autoFilledFields);

        // Set probability based on lead status (always auto-filled)
        if (selectedStatus && selectedStatus.probability !== undefined) {
            form.setFieldsValue({ probability: selectedStatus.probability });
            newAutoFilled.add('probability');
        }
        
        // Handle confirmed status based on lead status
        if (value === 8) {
            // Set status to In-Planning when lead is won
            form.setFieldsValue({ status: 'In-Planning' });
            newAutoFilled.add('status');
            
            // Set win date only if not already set (don't override user's manual input)
            const formValues = form.getFieldsValue();
            if (!formValues.winDate) {
                const precedingDates = [
                    formValues.leadDate,
                    formValues.proposalDate,
                    formValues.proposalFollowUpDate,
                    formValues.presentationDate
                ].filter(date => date && dayjs.isDayjs(date) && date.isValid()); // Remove null/undefined/invalid dates
                
                let winDate = dayjs(); // Start with today as default
                
                // Only use chain dates if today would violate the hierarchy
                if (precedingDates.length > 0) {
                    const latestPrecedingDate = precedingDates.reduce((latest, current) => 
                        current.isAfter(latest) ? current : latest
                    );
                    
                    // If today is before the latest preceding date, use that date instead
                    if (winDate.isBefore(latestPrecedingDate, 'day')) {
                        winDate = latestPrecedingDate;
                    }
                    // Otherwise, keep today as the win date
                }
                
                form.setFieldsValue({ winDate });
                newAutoFilled.add('winDate');
            }
            
            // Also auto-fill project start date if not already set
            if (!formValues.startDate) {
                // Get the current win date (either existing or newly set)
                const currentWinDate = form.getFieldsValue().winDate || dayjs();
                
                // Get all preceding dates including win date
                const allPrecedingDates = [
                    formValues.leadDate,
                    formValues.proposalDate,
                    formValues.proposalFollowUpDate,
                    formValues.presentationDate,
                    currentWinDate
                ].filter(date => date && dayjs.isDayjs(date) && date.isValid());
                
                let startDate = dayjs(); // Start with today as default
                
                // Only use chain dates if today would violate the hierarchy
                if (allPrecedingDates.length > 0) {
                    const latestPrecedingDate = allPrecedingDates.reduce((latest, current) => 
                        current.isAfter(latest) ? current : latest
                    );
                    
                    // If today is before the latest preceding date, use that date instead
                    if (startDate.isBefore(latestPrecedingDate, 'day')) {
                        startDate = latestPrecedingDate;
                    }
                    // Otherwise, keep today as the start date
                }
                
                form.setFieldsValue({ startDate });
                newAutoFilled.add('startDate');
            }
        } else {
            // Clear status when lead status is less than 8 (only if auto-filled)
            if (autoFilledFields.has('status')) {
                form.setFieldsValue({ status: undefined });
                newAutoFilled.delete('status');
            }
            
            // Clear win date when lead status is not 8 (only if auto-filled)
            if (autoFilledFields.has('winDate')) {
                form.setFieldsValue({ winDate: undefined });
                newAutoFilled.delete('winDate');
            }
            
            // Clear start date when lead status is not 8 (only if auto-filled)
            if (autoFilledFields.has('startDate')) {
                form.setFieldsValue({ startDate: undefined });
                newAutoFilled.delete('startDate');
            }
        }
        
        // Set dates based on lead status
        if (value === 1) {
            // For Lead (Qualified): Set lead date to today
            form.setFieldsValue({ leadDate: dayjs() });
            newAutoFilled.add('leadDate');
        }
        
        // Handle proposal date
        if (shouldAutofillProposalDateForLeadStatus(value)) {
            if (!form.getFieldValue('proposalDate')) {
                form.setFieldsValue({ proposalDate: dayjs() });
                newAutoFilled.add('proposalDate');
            }
        } else {
            if (autoFilledFields.has('proposalDate')) {
                form.setFieldsValue({ proposalDate: undefined });
                newAutoFilled.delete('proposalDate');
            }
        }
        
        // Update auto-filled fields tracking
        setAutoFilledFields(newAutoFilled);
        
        // Update required fields based on lead status
        form.validateFields();
    };

    // Validation for dates that would be disabled by hierarchy
    const validateDisabledDate = (currentField) => {
        return {
            validator: (_, value) => {
                if (!value || !dayjs.isDayjs(value) || !value.isValid()) {
                    return Promise.resolve(); // Skip validation if no value or invalid
                }
                
                // Use the same logic as getDisabledDate to check if this date would be disabled
                const isDisabled = getDisabledDate(currentField)(value);
                
                if (isDisabled) {
                    return Promise.reject(new Error('Date conflicts with project timeline'));
                }
                
                return Promise.resolve();
            }
        };
    };

    // Handle lead source change to show/hide referral input
    const handleLeadSourceChange = (value) => {
        handleManualFieldChange('leadSource');

        if (value === 'Referral') {
            setShowReferralInput(true);
        } else {
            setShowReferralInput(false);
            setReferralName('');
        }
    };

    // Function to trigger validation on all date fields when any date changes
    const validateAllDates = () => {
        const dateFields = ['leadDate', 'proposalDate', 'proposalFollowUpDate', 'presentationDate', 'winDate', 'startDate', 'endDate'];
        dateFields.forEach(field => {
            form.validateFields([field]).catch(() => {}); // Catch to prevent unhandled promise rejection
        });
    };

    // Date validation helper function - Updated to skip proposalFollowUpDate-presentationDate validation
    const getDisabledDate = (currentField) => {
        return (currentDate) => {
            if (!currentDate) return false;

            try {
                const formValues = form.getFieldsValue();

                // Special case for win date: if it's disabled by the form, don't apply additional validation
                if (currentField === 'winDate' && leadStatus !== 8) {
                    return false; // Let the disabled prop handle it
                }

                // Special handling for proposalFollowUpDate - it's independent of presentationDate
                if (currentField === 'proposalFollowUpDate') {
                    // Only check against leadDate and proposalDate
                    const precedingFields = ['leadDate', 'proposalDate'];
                    for (const fieldName of precedingFields) {
                        const fieldValue = formValues[fieldName];
                        if (fieldValue &&
                            dayjs.isDayjs(fieldValue) &&
                            fieldValue.isValid() &&
                            currentDate.isBefore(fieldValue, 'day')) {
                            return true; // Disable this date because it's before a preceding field
                        }
                    }
                    // Don't check against presentationDate or later fields
                    return false;
                }

                // For presentationDate, skip checking against proposalFollowUpDate
                if (currentField === 'presentationDate') {
                    // Check against leadDate and proposalDate only
                    const precedingFields = ['leadDate', 'proposalDate'];
                    for (const fieldName of precedingFields) {
                        const fieldValue = formValues[fieldName];
                        if (fieldValue &&
                            dayjs.isDayjs(fieldValue) &&
                            fieldValue.isValid() &&
                            currentDate.isBefore(fieldValue, 'day')) {
                            return true;
                        }
                    }

                    // Check against following fields (winDate, startDate, endDate)
                    const followingFields = ['winDate', 'startDate', 'endDate'];
                    for (const fieldName of followingFields) {
                        const fieldValue = formValues[fieldName];

                        // Skip validation against auto-filled dates
                        if ((fieldName === 'winDate' || fieldName === 'startDate') &&
                            autoFilledFields.has(fieldName)) {
                            continue;
                        }

                        if (fieldValue &&
                            dayjs.isDayjs(fieldValue) &&
                            fieldValue.isValid() &&
                            currentDate.isAfter(fieldValue, 'day')) {
                            return true;
                        }
                    }
                    return false;
                }

                // Standard date hierarchy for other fields: leadDate <= proposalDate <= presentationDate <= winDate <= startDate <= endDate
                // (excluding proposalFollowUpDate from the chain)
                const dateFields = [
                    'leadDate',
                    'proposalDate',
                    'presentationDate',
                    'winDate',
                    'startDate',
                    'endDate'
                ];

                const currentFieldIndex = dateFields.indexOf(currentField);
                if (currentFieldIndex === -1) return false;

                // Check against all preceding fields (must be >= all of them)
                for (let i = 0; i < currentFieldIndex; i++) {
                    const precedingFieldValue = formValues[dateFields[i]];
                    // Ensure the value exists, is a valid dayjs object, and is valid
                    if (precedingFieldValue &&
                        dayjs.isDayjs(precedingFieldValue) &&
                        precedingFieldValue.isValid() &&
                        currentDate.isBefore(precedingFieldValue, 'day')) {
                        return true; // Disable this date because it's before a preceding field
                    }
                }

                // Check against all following fields (must be <= all of them)
                // Special handling: if we're editing proposal-related dates and win date was auto-filled,
                // be more flexible to allow users to adjust the timeline
                for (let i = currentFieldIndex + 1; i < dateFields.length; i++) {
                    const followingFieldValue = formValues[dateFields[i]];
                    const followingFieldName = dateFields[i];

                    // Skip validation against auto-filled dates when editing proposal-related fields OR when editing win date
                    // This allows users to adjust proposal timeline even after setting lead status to 8
                    // and also allows editing win date when it's enabled
                    if ((followingFieldName === 'winDate' || followingFieldName === 'startDate') &&
                        autoFilledFields.has(followingFieldName) &&
                        (['proposalDate', 'presentationDate', 'winDate'].includes(currentField))) {
                        continue; // Skip this constraint to allow editing
                    }

                    // Ensure the value exists, is a valid dayjs object, and is valid
                    if (followingFieldValue &&
                        dayjs.isDayjs(followingFieldValue) &&
                        followingFieldValue.isValid() &&
                        currentDate.isAfter(followingFieldValue, 'day')) {
                        return true; // Disable this date because it's after a following field
                    }
                }

                return false; // Date is valid
            } catch (error) {
                logger.error('Date validation error:', error);
                return false; // If there's an error, don't disable the date
            }
        };
    };

    const onFinish = async (values) => {
        setLoading(true);
        
        try {
                logger.debug('Form values received:', values);
                logger.debug('Lead source from form:', values.leadSource);
                
                // Map form values to database field names
                const projectData = {
                // Basic project info
                name: values.name,
                description: values.description,
                type: isConfirmed ? 'CONFIRMED' : 'LEAD', // Set based on lead status
                status: isConfirmed ? 'In-Planning' : null,
                
                // Relations - map to correct foreign key names (convert to integers with validation)
                client_id: values.client ? parseInt(values.client) : null,
                person_in_charge_id: values.personInCharge ? parseInt(values.personInCharge) : null,
                client_person_in_charge_ids: values.clientPersonInCharge || [],
                currency_id: values.currency ? parseInt(values.currency) : null,
                
                // Lead information (convert to integers with validation)
                lead: isConfirmed ? 8 : (values.lead ? parseInt(values.lead) : 1), // Lead status (1-9), default to 1
                lead_source: values.leadSource === 'Referral' && referralName.trim()
                    ? `Referral - ${referralName.trim()}`
                    : values.leadSource || null,
                probability: isConfirmed ? 100 : (values.probability ? parseInt(values.probability) : 5),
                retainer: values.retainer === 'Yes' ? true : false, // Convert to boolean
                nonBillable: values.nonBillable === 'Yes' ? true : false, // Convert to boolean

                // Dates - convert to Date objects for Prisma
                start_date: values.startDate ? values.startDate.toDate() : null,
                end_date: values.endDate ? values.endDate.toDate() : null,
                lead_date: values.leadDate ? values.leadDate.toDate() : null,
                win_date: values.winDate ? values.winDate.toDate() : null,
                proposal_date: values.proposalDate ? values.proposalDate.toDate() : null,
                proposal_follow_up_date: values.proposalFollowUpDate ? values.proposalFollowUpDate.toDate() : null,
                presentation_date: !noPresentation && values.presentationDate ? values.presentationDate.toDate() : null,
                
                // Financial fields (convert to numbers)
                planned_revenue: values.plannedRevenue ? parseFloat(values.plannedRevenue) : null,
                estimated_vendor_cost: !noVendorCost && values.estimatedVendorCost ? parseFloat(values.estimatedVendorCost) : 0,

                // File and URL fields
                plan_url: values.planURL,
                plan_filename: values.planFilename || null,
                plan_filepath: values.planFilepath || null,

                // Boolean flags
                no_presentation: noPresentation,
                no_estimated_vendor_cost: noVendorCost
            };

            // Handle project classifications (many-to-many relationship) - convert to integers
            if (values.projectTypes && values.projectTypes.length > 0) {
                projectData.project_classifications = values.projectTypes.map(id => parseInt(id));
            }
            
            // Handle industry classifications (many-to-many relationship) - convert to integers
            if (values.projectIndustries && values.projectIndustries.length > 0) {
                projectData.industry_classifications = values.projectIndustries.map(id => parseInt(id));
            }
            
            // Handle checklists - convert to integers
            if (values.checklists && values.checklists.length > 0) {
                projectData.checklist_ids = values.checklists.map(id => parseInt(id));
            }

            logger.debug('Submitting project data:', projectData);
            logger.debug('Lead source in projectData:', projectData.lead_source);

            // Call API to create project
            const response = await nodeAPI.post('/project', projectData);
            logger.debug('Full API Response:', response);
            logger.debug('API Response Status:', response.status);
            logger.debug('API Response Data:', response.data);
            
            // Check for successful response
            if (response.data?.statusCode?.code === 1) {
                // Success based on API statusCode
                logger.info('Project created successfully!');
                form.resetFields();
                onClose(); // Close modal
                message.success('Project created successfully!');
                
                // Call success callback if provided
                if (onSuccess) {
                    onSuccess(response.data);
                }
                
                // Dispatch event for tab switching with slight delay
                setTimeout(() => {
                    logger.info('Dispatching project-created event for client project');
                    const event = new CustomEvent('project-created', { 
                        detail: { 
                            targetTab: leadStatus === 8 ? 'confirmed' : 'lead',
                            projectType: leadStatus === 8 ? 'CONFIRMED' : 'LEAD',
                            projectLead: leadStatus,
                            projectData: response.data?.data?.project,
                            refresh: true
                        } 
                    });
                    logger.debug('Event detail:', event.detail);
                    window.dispatchEvent(event);
                }, 100);
            } else {
                message.error(response.data?.statusCode?.msg || response.data?.message || 'Failed to create project');
            }
        } catch (error) {
            logger.error('Error creating project:', error);
            message.error('Failed to create project');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            visible={open}
            onClose={onClose}
            width="xl"
            className="client-project-form-modal"
        >
            <Modal.Header
                title={`ADD ${leadStatus === 8 ? 'CONFIRMED' : 'LEAD'} PROJECT`}
                actions={
                    <Button
                        variant="secondary"
                        size="small"
                        icon={<ArrowLeftOutlined />}
                        onClick={onBack}
                    >
                        Back to Selection
                    </Button>
                }
            />

            <Modal.Body padding="none">
                {/* Legend for auto-filled fields */}
                <div style={{ marginBottom: 20, marginTop: 16, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <div style={{ fontSize: 12, color: '#666' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{
                                width: 16,
                                height: 16,
                                backgroundColor: '#d4edda',
                                border: '1px solid #c3e6cb',
                                display: 'inline-block',
                                borderRadius: '2px'
                            }}></span>
                            <span>Auto-filled field (will update based on selections)</span>
                        </div>
                    </div>
                </div>
            
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    onFinishFailed={handleConditionalFailure(onFinish) || ((errorInfo) => {
                        logger.debug('Form validation failed:', errorInfo);
                        message.error('Please check all required fields');
                    })}
                    scrollToFirstError={{
                        behavior: 'smooth',
                        block: 'center'
                    }}
                    className="client-project-form"
                >
                {/* Row 1: Project Name, Project Types, PIC */}
                <Row gutter={24}>
                    <Col span={8}>
                        <Form.Item
                            label="Project name"
                            name="name"
                            rules={getConditionalRules('name', [{ required: true, message: 'Please enter project name' }])}
                        >
                            <Input placeholder="Enter project name" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Project classifications"
                            name="projectTypes"
                            rules={getConditionalRules('projectTypes', [{ required: true, message: 'Please select project classification' }])}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select project types"
                                loading={dataLoading}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {Array.isArray(projectTypes) && projectTypes.length > 0 ? (
                                    projectTypes.map(type => (
                                        <Option key={type.id} value={type.id}>
                                            {type.name || 'Unnamed Type'}
                                        </Option>
                                    ))
                                ) : dataLoading ? (
                                    <Option disabled>Loading project types...</Option>
                                ) : (
                                    <Option disabled>No project types available</Option>
                                )}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Person in charge"
                            name="personInCharge"
                            rules={getConditionalRules('personInCharge', [{ required: true, message: 'Please select PIC' }])}
                        >
                            <Select
                                placeholder="Select PIC"
                                showSearch
                                loading={dataLoading}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    // Get the text content safely
                                    const optionText = typeof option.children === 'string'
                                        ? option.children
                                        : String(option.children || '').replace('*', ''); // Remove asterisk for search
                                    
                                    return optionText.toLowerCase().includes(input.toLowerCase());
                                }}
                            >
                                {(() => {
                                    if (!Array.isArray(users) || users.length === 0) {
                                        return (
                                            <Option disabled>No users available</Option>
                                        );
                                    }
                                    
                                    // Separate active and inactive users based on status field
                                    const activeUsers = users.filter(u => u.status === 1 || u.status === '1');
                                    const inactiveUsers = users.filter(u => u.status !== 1 && u.status !== '1');
                                    
                                    return (
                                        <>
                                            {activeUsers.length > 0 && (
                                                <Select.OptGroup label="Active user">
                                                    {activeUsers.map(user => {
                                                        // Use full_name if available, otherwise combine first and last name
                                                        const displayName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User';
                                                        
                                                        return (
                                                            <Option key={user.id} value={user.id}>
                                                                {displayName}
                                                            </Option>
                                                        );
                                                    })}
                                                </Select.OptGroup>
                                            )}
                                            {inactiveUsers.length > 0 && (
                                                <Select.OptGroup label="Deactivated user">
                                                    {inactiveUsers.map(user => {
                                                        // Use full_name if available, otherwise combine first and last name
                                                        const displayName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || 'Unknown User';
                                                        
                                                        return (
                                                            <Option key={user.id} value={user.id} className="inactive-option">
                                                                {displayName}*
                                                            </Option>
                                                        );
                                                    })}
                                                </Select.OptGroup>
                                            )}
                                        </>
                                    );
                                })()}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Row 2: Client, Industries, Client PIC */}
                <Row gutter={24}>
                    <Col span={8}>
                        <Form.Item
                            label="Client"
                            name="client"
                            rules={getConditionalRules('client', [{ required: true, message: 'Please select client' }])}
                        >
                            <Select
                                placeholder="Select client"
                                showSearch
                                onChange={handleClientChange}
                                onSelect={handleClientSelect}
                                loading={dataLoading}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    // Get the text content safely
                                    const optionText = typeof option.children === 'string'
                                        ? option.children
                                        : String(option.children || '').replace('*', ''); // Remove asterisk for search
                                    
                                    return optionText.toLowerCase().includes(input.toLowerCase());
                                }}
                            >
                                {(() => {
                                    if (!Array.isArray(clients) || clients.length === 0) {
                                        return (
                                            <Option disabled>No clients available</Option>
                                        );
                                    }
                                    
                                    // Separate active and inactive clients
                                    const activeClients = clients.filter(c => c.is_active);
                                    const inactiveClients = clients.filter(c => !c.is_active);
                                    
                                    return (
                                        <>
                                            {activeClients.length > 0 && (
                                                <Select.OptGroup label="Active">
                                                    {activeClients.map(client => (
                                                        <Option key={client.id} value={client.id}>
                                                            {client.name || client.company_name || 'Unknown Client'}
                                                        </Option>
                                                    ))}
                                                </Select.OptGroup>
                                            )}
                                            {inactiveClients.length > 0 && (
                                                <Select.OptGroup label="Inactive">
                                                    {inactiveClients.map(client => (
                                                        <Option key={client.id} value={client.id} className="inactive-option">
                                                            {client.name || client.company_name || 'Unknown Client'}*
                                                        </Option>
                                                    ))}
                                                </Select.OptGroup>
                                            )}
                                        </>
                                    );
                                })()}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label="Client industry"
                            name="projectIndustries"
                            extra="From the selected client"
                            className={autoFilledFields.has('projectIndustries') ? 'auto-filled' : ''}
                        >
                            <Select
                                mode="multiple"
                                disabled
                                placeholder="Select industries"
                                loading={dataLoading}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                                onChange={() => {
                                    handleManualFieldChange('projectIndustries');
                                }}
                            >
                                {Array.isArray(industries) && industries.length > 0 ? (
                                    industries.map(industry => (
                                        <Option key={industry.id} value={industry.id}>
                                            {industry.name || 'Unnamed Industry'}
                                        </Option>
                                    ))
                                ) : dataLoading ? (
                                    <Option disabled>Loading industries...</Option>
                                ) : (
                                    <Option disabled>No industries available</Option>
                                )}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label={
                                <span>
                                    Client person in charge{' '}
                                    {clientContacts.length === 0 && (
                                        <Tooltip title="Add Client PIC data details first before add/edit project.">
                                            <InfoCircleOutlined style={{ color: '#dc3545' }} />
                                        </Tooltip>
                                    )}
                                </span>
                            }
                            name="clientPersonInCharge"
                            rules={getConditionalRules('clientPersonInCharge', [])}
                            className={autoFilledFields.has('clientPersonInCharge') ? 'auto-filled' : ''}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select client contacts"
                                disabled={!selectedClient || clientContacts.length === 0}
                                allowClear
                                showSearch
                                open={getDropdownOpen('clientPIC')}
                                onDropdownVisibleChange={(visible) => {
                                    setDropdownOpen('clientPIC', visible);
                                }}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    const optionText = typeof option.children === 'string'
                                        ? option.children
                                        : (Array.isArray(option.children) ? option.children.join('') : String(option.children || ''));
                                    return optionText.toLowerCase().includes(input.toLowerCase());
                                }}
                                onChange={() => {
                                    handleManualFieldChange('clientPersonInCharge');
                                }}
                            >
                                {Array.isArray(clientContacts) && clientContacts.length > 0 ? (
                                    clientContacts.map(contact => (
                                        <Option key={contact.id} value={contact.id}>
                                            {contact.fullName || `${contact.firstName} ${contact.lastName}`.trim() || 'Unknown Contact'}
                                        </Option>
                                    ))
                                ) : selectedClient && clientContacts.length === 0 ? (
                                    <Option disabled>No contacts found for this client</Option>
                                ) : (
                                    <Option disabled>Select a client first</Option>
                                )}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                {/* Row 3: Non-Billable, Retainer, Lead Status, Lead Source, Probability, Status */}
                <Row gutter={24}>
                    <Col span={isConfirmed ? 12 : 4}>
                        <Form.Item
                            label={<strong>Non-Billable</strong>}
                            name="nonBillable"
                            initialValue="No"
                            tooltip="Non-billable projects skip financial validation and are excluded from reports"
                        >
                            <Select>
                                <Option value="No">No</Option>
                                <Option value="Yes">Yes</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={isConfirmed ? 12 : 4}>
                        <Form.Item
                            label={<strong>Retainer</strong>}
                            name="retainer"
                            initialValue="No"
                        >
                            <Select>
                                <Option value="No">No</Option>
                                <Option value="Yes">Yes</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    {!isConfirmed && (
                    <Col span={6}>
                        <Form.Item
                            label="Lead status"
                            name="lead"
                            rules={[{ required: !isConfirmed, message: 'Please select lead status' }]}
                        >
                            <Select 
                                onChange={(value) => {
                                    handleLeadChange(value);
                                    // Force re-validation when lead status changes
                                    triggerRevalidation();
                                }}
                                placeholder="Type/Select one"
                            >
                                {Array.isArray(leadStatuses) && leadStatuses.map(status => (
                                    <Option
                                        key={status.id}
                                        value={status.id}
                                        disabled={status.id === 8 || status.lead === 8 || status.id === 9 || status.lead === 9}
                                    >
                                        {status.lead} - {status.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    )}
                    {!isConfirmed && (
                    <Col span={6}>
                        <Form.Item
                            label="Lead source"
                            name="leadSource"
                            rules={[{ required: leadStatus >= 1, message: 'Please select lead source' }]}
                            className={autoFilledFields.has('leadSource') ? 'auto-filled' : ''}
                        >
                            <Select
                                placeholder="Type/Select one"
                                onChange={handleLeadSourceChange}
                            >
                                {LEAD_SOURCE_OPTIONS.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        {showReferralInput && (
                            <Form.Item style={{ marginTop: -10, marginBottom: 24 }}>
                                <Input
                                    placeholder="Who referred this lead?"
                                    value={referralName}
                                    onChange={(e) => setReferralName(e.target.value)}
                                    maxLength={39}
                                />
                            </Form.Item>
                        )}
                    </Col>
                    )}
                    {!isConfirmed && (
                    <Col span={4}>
                        <Form.Item
                            label={<strong>% likely to win</strong>}
                            name="probability"
                            className={autoFilledFields.has('probability') ? 'auto-filled' : ''}
                        >
                            <Select
                                placeholder="Type/Select one"
                                disabled={leadStatus === 8 || leadStatus === 9}
                                onChange={() => {
                                    handleManualFieldChange('probability');
                                }}
                            >
                                {Array.isArray(probabilities) && probabilities.map(prob => (
                                    <Option
                                        key={prob}
                                        value={prob}
                                        disabled={prob === 0 || prob === 100}
                                    >
                                        {prob}%
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    )}

                </Row>

                {/* Row 4: Lead Date, Proposal Date, Follow-up Date, Presentation Date */}
                <Row gutter={24}>
                    {!isConfirmed && (
                    <Col span={8}>
                        <Form.Item
                            label="New lead date"
                            name="leadDate"
                            className={autoFilledFields.has('leadDate') ? 'auto-filled' : ''}
                            rules={[validateDisabledDate('leadDate')]}
                        >
                            <DatePicker 
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                showToday={true}
                                disabledDate={getDisabledDate('leadDate')}
                                onChange={() => {
                                    handleManualFieldChange('leadDate');
                                    validateAllDates();
                                }}
                            />
                        </Form.Item>
                    </Col>
                    )}
                    {!isConfirmed && (
                    <Col span={8}>
                        <Form.Item
                            label={
                                <span>
                                    Proposal sent date{' '}
                                    <Tooltip title="Planned date for proposal submission">
                                        <InfoCircleOutlined />
                                    </Tooltip>{' '}
                                    <Tooltip title="Date changes to upcoming dates will send an email and add an event to the calendars of the assigned PIC and project members.">
                                        <SendOutlined />
                                    </Tooltip>
                                </span>
                            }
                            name="proposalDate"
                            rules={[
                                { required: shouldRequireProposalDateForLeadStatus(leadStatus), message: 'Please select proposal date' },
                                validateDisabledDate('proposalDate')
                            ]}
                            className={autoFilledFields.has('proposalDate') ? 'auto-filled' : ''}
                        >
                            <DatePicker 
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                showToday={true}
                                disabledDate={getDisabledDate('proposalDate')}
                                onChange={() => {
                                    handleManualFieldChange('proposalDate');
                                    validateAllDates();
                                }}
                            />
                        </Form.Item>
                    </Col>
                    )}

                    {!isConfirmed && (
                    <Col span={8}>
                        <Form.Item
                            label={
                                <span>
                                    Client presentation date{' '}
                                    <Tooltip title="Date changes to upcoming dates will send an email and add an event to the calendars of the assigned PIC and project members.">
                                        <SendOutlined />
                                    </Tooltip>
                                </span>
                            }
                            name="presentationDate"
                            rules={[
                                { 
                                    required: shouldRequirePresentationDateForLeadStatus(leadStatus, noPresentation), 
                                    message: 'Please select presentation date' 
                                },
                                validateDisabledDate('presentationDate')
                            ]}
                            style={{ marginBottom: 8 }}
                        >
                            <DatePicker 
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                showToday={true}
                                disabled={noPresentation}
                                disabledDate={getDisabledDate('presentationDate')}
                                onChange={() => {
                                    validateAllDates();
                                }}
                            />
                        </Form.Item>
                        <Checkbox 
                            checked={noPresentation}
                            onChange={(e) => {
                                setNoPresentation(e.target.checked);
                                if (e.target.checked) {
                                    form.setFieldsValue({ presentationDate: null });
                                }
                            }}
                        >
                            No Client Presentation
                        </Checkbox>
                    </Col>
                    )}
                </Row>

                {/* Row 5: Win Date, Start Date, End Date */}
                <Row gutter={24}>
                    {isConfirmed && (
                    <Col span={8}>
                        <Form.Item
                            label="Project win date"
                            name="winDate"
                            className={autoFilledFields.has('winDate') ? 'auto-filled' : ''}
                            rules={[validateDisabledDate('winDate')]}
                        >
                            <DatePicker 
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                showToday={true}
                                disabled={leadStatus !== 8}
                                disabledDate={getDisabledDate('winDate')}
                                onChange={() => {
                                    handleManualFieldChange('winDate');
                                    validateAllDates();
                                }}
                            />
                        </Form.Item>
                    </Col>
                    )}
                    <Col span={isConfirmed ? 8 : 12}>
                        <Form.Item
                            label={
                                <span>
                                    Project start date
                                </span>
                            }
                            name="startDate"
                            rules={[
                                { required: leadStatus >= 2, message: 'Please select start date' },
                                validateDisabledDate('startDate')
                            ]}
                            className={autoFilledFields.has('startDate') ? 'auto-filled' : ''}
                        >
                            <DatePicker 
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                showToday={true}
                                disabledDate={getDisabledDate('startDate')}
                                onChange={() => {
                                    handleManualFieldChange('startDate');
                                    validateAllDates();
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={isConfirmed ? 8 : 12}>
                        <Form.Item
                            label={
                                <span>
                                    Project end date
                                </span>
                            }
                            name="endDate"
                            rules={[
                                { required: leadStatus >= 2, message: 'Please select end date' },
                                validateDisabledDate('endDate')
                            ]}
                        >
                            <DatePicker 
                                style={{ width: '100%' }}
                                format="DD/MM/YYYY"
                                showToday={true}
                                disabledDate={getDisabledDate('endDate')}
                                onChange={() => {
                                    validateAllDates();
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                {/* Row 6: Currency, Planned Revenue, Vendor Cost */}
                <Row gutter={24}>
                    <Col span={8}>
                        <Form.Item
                            label="Currency"
                            name="currency"
                            rules={getConditionalRules('currency', [{ required: true, message: 'Please select currency' }])}
                        >
                            <Select>
                                {Array.isArray(currencies) && currencies.map(currency => (
                                    <Option key={currency.id} value={currency.id}>
                                        {currency.iso} - {currency.name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label={
                                <span>
                                    Estimated project billings
                                </span>
                            }
                            name="plannedRevenue"
                            rules={[{ 
                                required: leadStatus >= 2, 
                                message: 'Please enter planned revenue' 
                            }]}
                        >
                            <ValidatedInputNumber
                                style={{ width: '100%' }}
                                placeholder="0.00"
                                formatter={(value) => smartDecimalFormatter(value, MONEY_AMOUNTS)}
                                parser={smartDecimalParser}
                                min={0}
                                step={0.01}
                                precision={MONEY_AMOUNTS.precision}
                                keyboard={false}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            label={
                                <span>
                                    Estimated vendors costs
                                </span>
                            }
                            name="estimatedVendorCost"
                            rules={[{ 
                                required: leadStatus >= 2 && !noVendorCost, 
                                message: 'Please enter vendor cost' 
                            }]}
                            style={{ marginBottom: 8 }}
                        >
                            <ValidatedInputNumber
                                style={{ width: '100%' }}
                                placeholder="0.00"
                                formatter={(value) => smartDecimalFormatter(value, MONEY_AMOUNTS)}
                                parser={smartDecimalParser}
                                min={0}
                                step={0.01}
                                precision={MONEY_AMOUNTS.precision}
                                keyboard={false}
                                disabled={noVendorCost}
                            />
                        </Form.Item>
                        <Checkbox 
                            checked={noVendorCost}
                            onChange={(e) => {
                                setNoVendorCost(e.target.checked);
                                if (e.target.checked) {
                                    form.setFieldsValue({ estimatedVendorCost: 0 });
                                }
                            }}
                        >
                            No Vendors Costs
                        </Checkbox>
                    </Col>
                </Row>

                {/* Row 7: Plan URL and Plan File */}
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item
                            label={
                                <span>
                                    GDrive folder URL
                                </span>
                            }
                            name="planURL"
                            rules={[{ 
                                required: leadStatus !== 1, 
                                message: 'Please enter GDrive folder URL' 
                            }]}
                        >
                            <Input placeholder="Enter Google Drive or other URL" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        {/* Hidden field to store the actual file path string */}
                        <Form.Item
                            name="planFilepath"
                            rules={[{
                                required: leadStatus === 8 && !useUrlForPlan,
                                message: 'Please upload project plan'
                            }]}
                            hidden
                        >
                            <Input />
                        </Form.Item>
                        {/* Hidden field to store the filename */}
                        <Form.Item name="planFilename" hidden>
                            <Input />
                        </Form.Item>

                        <Form.Item
                            label={
                                <span>
                                    <strong>Project plan</strong>{' '}
                                    {leadStatus === 8 && !useUrlForPlan && <span style={{ color: '#ff4d4f' }}>*</span>}{' '}
                                    <Tooltip title="Max Size 10MB, Supported File: .Jpg, .Jpeg, .Png, .Gif, .Webp, .Pdf, .Xls, .Xlsx, .Ppt, .Pptx">
                                        <InfoCircleOutlined />
                                    </Tooltip>
                                </span>
                            }
                            style={{ marginBottom: 8 }}
                            extra={<span className="project-plan-guide-text">{PROJECT_PLAN_GUIDE_TEXT}</span>}
                            help={form.getFieldError('planFilepath')?.[0]}
                            validateStatus={form.getFieldError('planFilepath')?.length ? 'error' : undefined}
                        >
                            {!useUrlForPlan ? (
                                <Upload
                                    maxCount={1}
                                    beforeUpload={async (file) => {
                                        // Validate file size
                                        const isLt10M = file.size / 1024 / 1024 < 10;
                                        if (!isLt10M) {
                                            message.error('File must be smaller than 10MB!');
                                            return false;
                                        }

                                        // Validate file type
                                        const allowedTypes = [
                                            'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
                                            'application/pdf',
                                            'application/vnd.ms-excel',
                                            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                                            'application/vnd.ms-powerpoint',
                                            'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                                        ];

                                        if (!allowedTypes.includes(file.type)) {
                                            message.error('Invalid file type. Supported: JPG, PNG, GIF, WebP, PDF, XLS, XLSX, PPT, PPTX');
                                            return false;
                                        }

                                        // Upload using nodeAPI (manual upload pattern)
                                        try {
                                            const formData = new FormData();
                                            formData.append('file', file);

                                            message.loading(`Uploading ${file.name}...`, 0);

                                            const uploadResponse = await nodeAPI.post('/project/upload/plan', formData, {
                                                headers: { 'Content-Type': 'multipart/form-data' }
                                            });

                                            message.destroy(); // Clear loading message

                                            // Handle response format from lib-upload.js
                                            if (uploadResponse.data?.statusCode?.code === 1) {
                                                const responseData = uploadResponse.data.data || uploadResponse.data;
                                                const s3Path = responseData?.path;
                                                const originalFileName = file.name;

                                                if (s3Path) {
                                                    message.success(`${file.name} uploaded successfully`);

                                                    // Set the form values
                                                    form.setFieldsValue({
                                                        planFilepath: s3Path,
                                                        planFilename: originalFileName
                                                    });
                                                } else {
                                                    message.error('Upload succeeded but could not process response');
                                                }
                                            } else {
                                                const errorMsg = uploadResponse.data?.statusCode?.msg ||
                                                               uploadResponse.data?.statusCode?.debug ||
                                                               'Invalid response format';
                                                message.error('Upload failed: ' + errorMsg);
                                            }
                                        } catch (uploadError) {
                                            message.destroy();
                                            logger.error('Upload error:', uploadError);
                                            message.error(`Upload failed: ${file.name}`);
                                        }

                                        return false; // Prevent automatic upload
                                    }}
                                >
                                    <Button
                                        variant="secondary"
                                        icon={<UploadOutlined />}
                                        style={{ width: '100%' }}
                                    >
                                        Choose file
                                    </Button>
                                </Upload>
                            ) : (
                                <Input 
                                    placeholder="Enter file URL"
                                    name="planFileUrl"
                                />
                            )}
                        </Form.Item>
                        <Checkbox 
                            checked={useUrlForPlan}
                            onChange={(e) => setUseUrlForPlan(e.target.checked)}
                        >
                            Use URL
                        </Checkbox>
                    </Col>
                </Row>

                {/* Row 8: Description and Checklists */}
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            label="Description"
                            name="description"
                        >
                            <TextArea 
                                placeholder="Enter project description..."
                                rows={6}
                                style={{ 
                                    resize: 'vertical !important', 
                                    minHeight: '150px !important',
                                    maxHeight: '400px !important',
                                    overflow: 'auto !important'
                                }}
                            />
                        </Form.Item>
                    </Col>

                </Row>
            </Form>
            </Modal.Body>

            <Modal.Footer
                buttons={[
                    {
                        ...BUTTON_PRESETS.CANCEL,
                        key: 'cancel',
                        label: 'Cancel',
                        onClick: onClose
                    },
                    {
                        ...BUTTON_PRESETS.SAVE,
                        key: 'create',
                        label: 'CREATE PROJECT',
                        onClick: async () => {
                            try {
                                const values = await validateConditionally();
                                onFinish(values);
                            } catch (error) {
                                logger.debug('Validation failed:', error);
                                if (error.message) {
                                    message.error(error.message);
                                }
                            }
                        },
                        loading: loading,
                        disabled: loading || dataLoading || !clientDetailsReady
                    }
                ]}
            />
        </Modal>
    );
};

export default ClientProjectForm;
